import React, { useEffect, useState, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { authApi } from "api/auth/index.js";
import Globales from "utils/Globales";
import { CircularProgress } from "@material-ui/core";
import { useUserStore } from "stores/userStore";
import { useDesingStore } from "stores/desingStore";
import ModalNotifiacion from "../Generales/Header/ModalNotifiacion.jsx";

const LoginButton = () => {
	const navigate = useNavigate();
	const { userState, setUser } = useUserStore();
	const { desingState, setDesingState } = useDesingStore();
	const {
		loginWithPopup,
		loginWithRedirect,
		user,
		isLoading,
		logout,
		isAuthenticated,
	} = useAuth0();
	const [cargando, setCargando] = useState(false);
	const [loginIniciado, setLoginIniciado] = useState(false);
	const [procesoCompletado, setProcesoCompletado] = useState(false);
	const [reintentos, setReintentos] = useState(0);
	const [mostrarModalValidacion, setMostrarModalValidacion] = useState(false);
	const modalEnviadoRef = useRef(false); // Para evitar envío duplicado
	const timeoutRef = useRef(null);
	const isInPopup = window.opener !== null;

	// El estado global ya tiene la bandera establecida por Diseñador.jsx cuando se accede a esa ruta
	// No necesitamos leer de localStorage/sessionStorage porque usamos el estado global

	// 🔥 Iniciar login automáticamente si no está autenticado
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (!loginIniciado && !isAuthenticated && !isLoading) {
				// Verificar el estado global antes de iniciar login
				const returnToCheck = desingState?.returnToDiseñador === true;
				console.log(
					"🔍 Verificando antes de iniciarLogin - returnToDiseñador en estado global:",
					returnToCheck
				);

				setLoginIniciado(true);
				iniciarLogin();
			}
		}, 100); // Pequeño delay para asegurar que el useEffect anterior se ejecute

		return () => clearTimeout(timeoutId);
	}, [loginIniciado, isAuthenticated, isLoading]);

	// 🔥 Función para iniciar login
	const iniciarLogin = async () => {
		try {
			setCargando(true);

			// Verificar si viene del diseñador ANTES de redirigir
			// Esto es crítico porque Auth0 puede perder los parámetros de la URL
			let vieneDelDiseñador =
				localStorage.getItem("returnToDiseñador") === "true";
			let paramsPendientes = localStorage.getItem("diseñadorParamsPendiente");

			// También verificar sessionStorage como respaldo
			if (!vieneDelDiseñador) {
				const returnToSession = sessionStorage.getItem("returnToDiseñador");
				if (returnToSession === "true") {
					localStorage.setItem("returnToDiseñador", "true");
					vieneDelDiseñador = true;
					console.log(
						"✅ Recuperado returnToDiseñador desde sessionStorage en iniciarLogin"
					);
				}
			}

			if (!paramsPendientes) {
				const paramsSession = sessionStorage.getItem(
					"diseñadorParamsPendiente"
				);
				if (paramsSession) {
					localStorage.setItem("diseñadorParamsPendiente", paramsSession);
					paramsPendientes = paramsSession;
					console.log(
						"✅ Recuperado diseñadorParamsPendiente desde sessionStorage en iniciarLogin"
					);
				}
			}

			console.log(
				"🔍 iniciarLogin - vieneDelDiseñador:",
				vieneDelDiseñador,
				"paramsPendientes:",
				paramsPendientes ? "presente" : "ausente"
			);

			// Si no está en localStorage pero está en la URL, guardarlo ANTES de redirigir
			if (!vieneDelDiseñador) {
				const search = window.location.search || "";
				if (search) {
					const urlParams = new URLSearchParams(search);
					const returnToUrl = urlParams.get("returnTo");
					const paramsUrl = urlParams.get("params");

					if (returnToUrl === "diseñador3D") {
						localStorage.setItem("returnToDiseñador", "true");
						sessionStorage.setItem("returnToDiseñador", "true");
						if (paramsUrl) {
							localStorage.setItem("diseñadorParamsPendiente", paramsUrl);
							sessionStorage.setItem("diseñadorParamsPendiente", paramsUrl);
						}
						console.log(
							"✅ Guardado returnToDiseñador desde URL ANTES de redirigir a Auth0 (localStorage + sessionStorage)"
						);
						vieneDelDiseñador = true;
						if (paramsUrl) {
							paramsPendientes = paramsUrl;
						}
					}
				}
			}

			// Verificar de nuevo después de guardar desde URL
			const vieneDelDiseñadorFinal =
				localStorage.getItem("returnToDiseñador") === "true";
			const paramsPendientesFinal = localStorage.getItem(
				"diseñadorParamsPendiente"
			);

			if (vieneDelDiseñadorFinal) {
				console.log(
					"✅ Usando loginWithRedirect con appState para mantener parámetros"
				);
				// Usar loginWithRedirect (sin popup) cuando viene del diseñador
				// IMPORTANTE: Usar appState para que Auth0 mantenga los parámetros
				await loginWithRedirect({
					authorizationParams: {
						audience: import.meta.env.VITE_AUTH0_AUDIENCE,
						scope: "openid profile email offline_access",
						redirect_uri: window.location.origin + "/login",
						prompt: "select_account",
					},
					appState: {
						returnTo: "diseñador3D",
						params: paramsPendientesFinal || paramsPendientes,
					},
				});
			} else if (isInPopup) {
				await loginWithRedirect({
					authorizationParams: {
						audience: import.meta.env.VITE_AUTH0_AUDIENCE,
						scope: "openid profile email offline_access",
						prompt: "select_account",
					},
				});
			} else {
				await loginWithPopup({
					authorizationParams: {
						audience: import.meta.env.VITE_AUTH0_AUDIENCE,
						scope: "openid profile email offline_access",
						prompt: "select_account",
					},
				});
			}
		} catch (error) {
			setCargando(false);
		}
	};

	// 🔥 Procesar login después de autenticación
	useEffect(() => {
		const continuar = async () => {
			if (procesoCompletado) {
				return;
			}

			// Esperar a que Auth0 termine de procesar
			if (isLoading || !user || !isAuthenticated) {
				if (reintentos >= 10) {
					return;
				}

				setReintentos((prev) => prev + 1);
				timeoutRef.current = setTimeout(() => {
					continuar();
				}, 3000);
				return;
			}

			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}

			if (!isLoading && user && isAuthenticated) {
				// Verificar returnToDiseñador desde el estado global
				const returnToDiseñadorGlobal = desingState?.returnToDiseñador === true;
				console.log(
					"🔍 Iniciando proceso de login - returnToDiseñador en estado global:",
					returnToDiseñadorGlobal
				);

				try {
					setProcesoCompletado(true);
					setCargando(true);

					// Si el email está verificado en Auth0, verificar automáticamente en nuestro backend
					if (user.email_verified) {
						try {
							const verifyResponse = await fetch(
								`${Globales.Url}ecommerce/auth/verify-email`,
								{
									method: "POST",
									headers: {
										"Content-Type": "application/json",
									},
									body: JSON.stringify({
										auth0UserId: user.sub,
									}),
								}
							);

							// Esperar un momento para que la actualización se complete
							await new Promise((resolve) => setTimeout(resolve, 500));
						} catch (err) {
							// Silenciar error, continuar con flujo normal
							console.log("Error al verificar email automáticamente:", err);
						}
					}

					// Validar cuenta
					const validar = await authApi.ValidateAcount({
						socialToken: user.sub,
						Email: user.email,
						name: user.name,
					});

					console.log("🔍 Validación de cuenta:", {
						success: validar.success,
						returnToDiseñador: desingState?.returnToDiseñador,
					});

					if (!validar.success) {
						// Verificar si necesita validación de correo
						const needsValidation =
							(validar.data && validar.data.needsValidation) ||
							(validar.errors && validar.errors.needsValidation);

						const mensajeEsValidacion =
							validar.message &&
							validar.message.toLowerCase().includes("valide tu correo");

						// IMPORTANTE: NO mostrar modal si el email ya está verificado en Auth0
						// El endpoint verify-email ya debería haber actualizado Pendiente: false
						if (
							(needsValidation || mensajeEsValidacion) &&
							!user.email_verified
						) {
							// Verificar si ya se mostró el modal antes (usando localStorage)
							const modalYaMostrado = localStorage.getItem(
								`modalVerificacion_${user.sub}`
							);

							// Verificar si viene del diseñador usando el estado global
							const returnToDiseñador = desingState?.returnToDiseñador === true;

							if (!modalEnviadoRef.current && !modalYaMostrado) {
								modalEnviadoRef.current = true;

								// NO marcar en localStorage todavía - solo cuando el usuario cierre el modal
								// Esto permite que el modal se muestre correctamente

								// NO enviar correo desde frontend - Auth0 ya lo envió
								// El correo fue enviado automáticamente por Auth0 cuando el usuario se registró

								// Mostrar modal informativo
								// El usuario puede cerrarlo y automáticamente iniciará login
								setMostrarModalValidacion(true);

								// Si viene del diseñador, NO bloquear completamente el flujo
								// Permitir que continúe para asociar diseños y preparar redirección
								if (returnToDiseñador) {
									// Continuar con el flujo pero mostrar el modal
									// El modal se cerrará y redirigirá al diseñador
									console.log(
										"⚠️ Usuario viene del diseñador pero necesita verificar email. Continuando flujo..."
									);
									// NO hacer return aquí, permitir que continúe
								} else {
									// Si NO viene del diseñador, detener el proceso aquí
									setCargando(false);
									return;
								}
							}
							// Si ya se mostró antes, continuar con login normalmente
							// NO retornar aquí - Permitir continuar con login
							// El backend permitirá login aunque Pendiente: true
						} else if (needsValidation || mensajeEsValidacion) {
							// Email está verificado en Auth0 pero aún Pendiente: true en BD
							// No mostrar modal, solo continuar con login
							// El Post-Login Action actualizará Pendiente: false
							console.log(
								"Email verificado en Auth0, continuando con login..."
							);
						} else {
							// Otro tipo de error
							setCargando(false);
							setProcesoCompletado(false);
							return;
						}
					}

					// Login en backend
					// Ahora permite login aunque Pendiente: true
					// El Post-Login Action se encargará de actualizar Pendiente: false
					const returnToDiseñadorCheck =
						desingState?.returnToDiseñador === true;
					console.log(
						"🔐 Iniciando loginBackend - returnToDiseñador:",
						returnToDiseñadorCheck
					);
					const sesionInfo = await loginBackend(user.sub);
					console.log(
						"✅ loginBackend completado:",
						sesionInfo ? "éxito" : "falló"
					);
					if (!sesionInfo) {
						console.error("❌ Error en loginBackend");
						// Si viene del diseñador, intentar redirigir de todas formas
						if (returnToDiseñadorCheck) {
							console.log(
								"⚠️ Login falló pero viene del diseñador, redirigiendo de todas formas..."
							);
							setDesingState({ returnToDiseñador: false });
							setCargando(false);
							window.location.href = window.location.origin + "/diseñador3D";
							return;
						}
						setCargando(false);
						setProcesoCompletado(false);
						return;
					}

					// Asociar diseños de sessionId a idCliente si existe sessionId
					const sessionId = localStorage.getItem("sessionId");
					const returnToDiseñador = desingState?.returnToDiseñador === true;

					// Si viene del diseñador, guardar parámetros en el estado global si están en localStorage
					if (returnToDiseñador) {
						const paramsPendientes = localStorage.getItem(
							"diseñadorParamsPendiente"
						);
						if (paramsPendientes) {
							try {
								const params = JSON.parse(paramsPendientes);
								setDesingState({
									diseñadorParamsPendiente: params,
									returnToDiseñador: true,
								});
								console.log(
									"✅ Parámetros del diseñador guardados en estado global durante login"
								);
							} catch (e) {
								console.error("Error al parsear parámetros del diseñador:", e);
							}
						}
					}

					if (sessionId && sesionInfo && sesionInfo.id) {
						// Validar que idCliente sea un ObjectId válido (24 caracteres hexadecimales)
						const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(sesionInfo.id);

						if (!isValidObjectId) {
							// No intentar asociar si el idCliente no es válido
							localStorage.removeItem("sessionId");
							// Si viene del diseñador, NO hacer return aquí, continuar con redirección
							if (!returnToDiseñador) {
								return;
							}
						}

						try {
							const asociarResponse = await fetch(
								`${
									Globales.Url
								}DisenoProductos/DisenoProductos/AsociarDisenosASesion`,
								{
									method: "POST",
									headers: {
										"Content-Type": "application/json",
									},
									body: JSON.stringify({
										sessionId: sessionId,
										idCliente: sesionInfo.id,
									}),
								}
							);

							const asociarData = await asociarResponse.json();

							if (!asociarData.error && asociarData.data?.cantidad > 0) {
								// Guardar el ID del último diseño asociado para cargarlo después
								if (
									asociarData.data.diseñosAsociados &&
									asociarData.data.diseñosAsociados.length > 0
								) {
									const ultimoDisenoId =
										asociarData.data.diseñosAsociados[
											asociarData.data.diseñosAsociados.length - 1
										];
									localStorage.setItem(
										"ultimoDisenoAsociado",
										ultimoDisenoId.toString()
									);
									localStorage.setItem("cargarDisenoDespuesLogin", "true");
								}
							}

							// Limpiar sessionId después de asociar
							localStorage.removeItem("sessionId");
						} catch (error) {
							// No bloquear el flujo si falla la asociación
							localStorage.removeItem("sessionId");
						}
					}

					// Verificar si viene del diseñador usando el estado global (ya declarado arriba)
					console.log(
						"🔍 Verificando redirección - returnToDiseñador en estado global:",
						returnToDiseñador
					);
					console.log(
						"🔍 Estado actual - procesoCompletado:",
						procesoCompletado,
						"cargando:",
						cargando
					);

					if (returnToDiseñador) {
						console.log(
							"✅ Redirigiendo al diseñador después de login exitoso"
						);
						// Limpiar la bandera del estado global
						setDesingState({ returnToDiseñador: false });
						// Marcar proceso como completado
						setProcesoCompletado(true);
						setCargando(false);

						// Redirigir de vuelta al diseñador
						// Usar window.location.href directamente (más confiable que navigate para redirección completa)
						console.log("🚀 Ejecutando redirección a /diseñador3D");
						window.location.href = window.location.origin + "/diseñador3D";
						return; // Importante: salir aquí para evitar otras redirecciones
					} else if (!window.opener) {
						console.log("📍 Redirigiendo a Perfil (no viene del diseñador)");
						setCargando(false);
						setProcesoCompletado(true);
						navigate("/Perfil");
					} else {
						console.log("📍 No hay redirección necesaria (popup)");
						setCargando(false);
						setProcesoCompletado(true);
					}
				} catch (error) {
					setCargando(false);
					setProcesoCompletado(false);
				}
			}
		};

		continuar();

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [
		isLoading,
		user,
		isAuthenticated,
		procesoCompletado,
		reintentos,
		navigate,
	]);

	const cerrarModalValidacion = async () => {
		setMostrarModalValidacion(false);

		// Marcar que el modal fue cerrado para evitar que se vuelva a abrir
		if (user?.sub) {
			localStorage.setItem(`modalVerificacion_${user.sub}`, "true");
		}

		// Verificar si viene del diseñador usando el estado global
		const returnToDiseñador = desingState?.returnToDiseñador === true;

		if (returnToDiseñador) {
			console.log(
				"✅ Usuario viene del diseñador, continuando flujo después de cerrar modal..."
			);

			// Si el usuario ya está autenticado, reiniciar el proceso para que continúe el flujo
			if (isAuthenticated && user) {
				// Reiniciar el proceso para que continúe desde donde se quedó
				setProcesoCompletado(false);
				setCargando(true);
				// El useEffect se ejecutará de nuevo y continuará el flujo
				return;
			}
		}

		// Iniciar login automáticamente para que el usuario vaya a aceptar el consentimiento
		// Esto mejorará la UX: usuario cierra modal → automáticamente va a login → acepta consentimiento → inicia sesión
		try {
			setCargando(true);

			// Usar loginWithRedirect para que vaya a Auth0 y acepte el consentimiento
			await loginWithRedirect({
				authorizationParams: {
					audience: import.meta.env.VITE_AUTH0_AUDIENCE,
					scope: "openid profile email offline_access",
					redirect_uri: window.location.origin + "/login",
					prompt: "select_account",
				},
			});
		} catch (error) {
			console.error("Error al iniciar login automático:", error);
			setCargando(false);
		}
	};

	const loginBackend = async (socialToken) => {
		try {
			const resp = await authApi.Auth({ socialToken });

			if (!resp.success) {
				logout();
				return null;
			}

			// Validar que user._id exista y sea un ObjectId válido
			if (!resp.data?.user?._id) {
				logout();
				return null;
			}

			const userId = resp.data.user._id.toString();
			const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(userId);

			if (!isValidObjectId) {
				logout();
				return null;
			}

			const sesion = {
				id: userId,
				RedSocial: resp.data.user.RedSocial,
				Carrito: resp.data.basket,
				ListaCarrito: resp.data.user.ListaCarrito,
				ListaDeseos: resp.data.user.ListaDeseos,
				Usuario: resp.data.user.Usuario,
				Nombre: resp.data.user.Nombre,
				Correo: resp.data.user.Correo,
				Telefono: resp.data.user.Telefono,
				TipoUsuario: resp.data.user.TipoUsuario,
				Token: resp.data.token,
				IndicadorTerco: resp.data.user.IndicadorTerco,
			};
			setUser(sesion);
			let dt = new Date();
			localStorage.setItem("expira", new Date(dt.getTime() + 20 * 60000));

			return sesion;
		} catch (error) {
			return null;
		}
	};

	if (cargando) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
				}}
			>
				<CircularProgress size={70} thickness={5} style={{ color: "black" }} />
			</div>
		);
	}

	return (
		<div>
			{mostrarModalValidacion ? (
				<ModalNotifiacion
					Show={mostrarModalValidacion}
					eventClick={cerrarModalValidacion}
					mensaje=""
					color="warning"
					titulo=""
				/>
			) : (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100vh",
					}}
				>
					<CircularProgress
						size={70}
						thickness={5}
						style={{ color: "black" }}
					/>
				</div>
			)}
		</div>
	);
};

export default LoginButton;
